<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  customAttributes: { type: Array, default: () => [] },
});

const filters = ref({ id: '', name: '', phone: '' });
const openCol = ref(null); // 'id' | 'name' | 'phone' | null
const popoverStyle = ref({});
const inputRef = ref(null);

const COLS = [
  { key: 'id', label: 'Id' },
  { key: 'name', label: 'Nome' },
  { key: 'phone', label: 'Telefone' },
];

const validRows = computed(() => props.rows.filter(r => !(r.errors || []).length));

const filtered = computed(() => {
  return validRows.value.filter(r => {
    if (filters.value.id    && !String(r.line).includes(filters.value.id)) return false;
    if (filters.value.name  && !String(r.name || '').toLowerCase().includes(filters.value.name.toLowerCase())) return false;
    if (filters.value.phone && !String(r.phone_number || '').includes(filters.value.phone)) return false;
    return true;
  });
});

async function toggle(col, evt) {
  if (openCol.value === col) {
    closeAll();
    return;
  }
  const btn = evt.currentTarget;
  const r = btn.getBoundingClientRect();
  // Place popover below the icon, right-aligned to it. Use position: fixed so
  // the body teleport doesn't get clipped by the table wrapper's overflow.
  popoverStyle.value = {
    position: 'fixed',
    top: `${Math.round(r.bottom + 6)}px`,
    left: `${Math.round(Math.max(8, r.right - 220))}px`,
    minWidth: '220px',
  };
  openCol.value = col;
  await nextTick();
  inputRef.value?.focus();
}
function closeAll() {
  openCol.value = null;
}
function onDocClick(e) {
  if (e.target.closest('.sz-filter-popover-portal') || e.target.closest('.sz-filter-icon')) return;
  closeAll();
}
function onEsc(e) {
  if (e.key === 'Escape') closeAll();
}
function onScroll() { closeAll(); }

onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onEsc);
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onScroll);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onEsc);
  window.removeEventListener('scroll', onScroll, true);
  window.removeEventListener('resize', onScroll);
});
</script>

<template>
  <div class="sz-step-preview">
    <h4>Pré-visualizar contatos</h4>
    <div class="sz-preview-table-wrapper">
      <table class="sz-preview-table">
        <thead>
          <tr>
            <th v-for="col in COLS" :key="col.key">
              <div class="sz-th-inner">
                <span class="sz-th-label">{{ col.label }}</span>
                <button
                  type="button"
                  class="sz-filter-icon"
                  :class="{ 'sz-filter-icon-on': filters[col.key] || openCol === col.key }"
                  :aria-label="`Filtrar ${col.label}`"
                  @click.stop="toggle(col.key, $event)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="4"  y1="7"  x2="20" y2="7"  />
                    <line x1="7"  y1="12" x2="17" y2="12" />
                    <line x1="10" y1="17" x2="14" y2="17" />
                  </svg>
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(r, i) in filtered" :key="r.line">
            <td>{{ i + 1 }}</td>
            <td>{{ r.name }}</td>
            <td>{{ r.phone_number }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="sz-preview-counter">
      Exibindo {{ filtered.length }} de {{ validRows.length }} contatos válidos
    </p>

    <Teleport to="body">
      <div
        v-if="openCol"
        class="sz-filter-popover-portal"
        :style="popoverStyle"
        @click.stop
      >
        <input
          ref="inputRef"
          v-model="filters[openCol]"
          type="text"
          :placeholder="`Filtrar ${COLS.find(c => c.key === openCol)?.label.toLowerCase()}`"
          @keydown.enter="closeAll"
        />
        <button
          v-if="filters[openCol]"
          type="button"
          class="sz-filter-clear"
          @click="filters[openCol] = ''"
        >Limpar</button>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
h4 { margin: 0 0 14px; font-size: 14px; font-weight: 600; }

.sz-preview-table-wrapper {
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 10px;
  max-height: 50vh; overflow: auto;
}
.sz-preview-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
}
.sz-preview-table thead {
  background: #f3f4f6; position: sticky; top: 0; z-index: 1;
}
.sz-preview-table th, .sz-preview-table td {
  text-align: left; padding: 12px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.sz-th-inner {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  font-weight: 500;
}
.sz-th-label { color: #18181b; }

.sz-filter-icon {
  border: none; background: transparent; padding: 4px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 6px; cursor: pointer; color: #71717a;
  transition: background 0.15s, color 0.15s;
}
.sz-filter-icon:hover { background: rgba(0,0,0,0.06); color: #18181b; }
.sz-filter-icon-on { color: #16a34a; }

.sz-preview-counter {
  margin-top: 10px; color: #71717a; font-size: 12px;
}
</style>

<style lang="scss">
/* Unscoped — popover is teleported to <body> and would otherwise miss the
   component's data-v attribute (same pattern as sz-sticker-delete-modal). */
.sz-filter-popover-portal {
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  padding: 8px;
  z-index: 100001;
  display: flex; gap: 6px; align-items: center;
  font-family: inherit;
}
.sz-filter-popover-portal input {
  flex: 1; min-width: 0;
  padding: 7px 10px; font-size: 12px;
  border: 1px solid rgba(0,0,0,0.12); border-radius: 6px;
  background: #ffffff;
  outline: none;
}
.sz-filter-popover-portal input:focus {
  border-color: #16a34a;
  box-shadow: 0 0 0 2px rgba(22,163,74,0.15);
}
.sz-filter-clear {
  padding: 6px 10px;
  font-size: 11px; border: none; background: transparent;
  color: #16a34a; cursor: pointer; font-weight: 500; white-space: nowrap;
}
.dark .sz-filter-popover-portal,
body.dark .sz-filter-popover-portal {
  background: #2a2d32;
  border-color: rgba(255,255,255,0.1);
  box-shadow: 0 6px 20px rgba(0,0,0,0.4);
}
.dark .sz-filter-popover-portal input,
body.dark .sz-filter-popover-portal input {
  background: #1f2125; color: #fafafa;
  border-color: rgba(255,255,255,0.12);
}
</style>
